import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { api } from '../services/api';

export interface IOpeningHoursRange {
  open: string | null;
  close: string | null;
}

export interface IOpeningHours {
  weekdays: IOpeningHoursRange;
  saturday: IOpeningHoursRange;
  sunday: IOpeningHoursRange;
}

export interface IUser {
  id: string;
  fullname: string;
  nickname: string;
  username: string;
  email: string;
  phone: string | undefined;
  birthday: Date | undefined;
  gym_id: string;
  avatar: string | undefined;
  avatar_url: string | undefined;
  permissions: string[];
  features: Record<string, number | null>;
  is_active: boolean;
  privacity: boolean;
  min_age: boolean;
  terms_accepted: boolean;
  created_at: Date;
  updated_at: Date;
  gym: {
    id: string;
    name: string;
    domain: string;
    logo: string | undefined;
    logo_url: string | undefined;
    phone: string | undefined;
    address: string | undefined;
    description: string | undefined;
    cnpj: string | undefined;
    instagram: string | undefined;
    address_number: string | undefined;
    address_zipcode: string | undefined;
    address_city: string | undefined;
    address_uf: string | undefined;
    capacity: number | undefined;
    opening_hours: IOpeningHours | null | undefined;
    signature: string;
    is_active: boolean;
    created_at: Date;
    updated_at: Date;
  };
}

export interface IMembership {
  gym_id: string;
  gym_name: string;
  permissions: string[];
}

export type ViewMode = 'platform' | 'gym';

interface SignInCredentials {
  user: IUser;
  token: string;
  refresh_token?: string;
}

interface AuthContextData {
  user: IUser;
  token: string;
  memberships: IMembership[];
  activeGymId: string | undefined;
  switchGym(gym_id: string): Promise<void>;
  viewMode: ViewMode;
  setViewMode(mode: ViewMode): void;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: IUser): Promise<void>;
  loading: boolean;
  setLoading(set: boolean): void;
  loadingStoragedData(): Promise<void>;
}

interface AuthState {
  token: string;
  user: IUser;
}

const ACTIVE_GYM_STORAGE_KEY = '@TrenzoX:activeGymId';
const VIEW_MODE_STORAGE_KEY = '@TrenzoX:viewMode';
const REFRESH_TOKEN_STORAGE_KEY = '@TrenzoX:refreshToken';

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<IUser>({} as IUser);
  const [userToken, setUserToken] = useState<string>('');
  const [memberships, setMemberships] = useState<IMembership[]>([]);
  const [activeGymId, setActiveGymId] = useState<string | undefined>(
    localStorage.getItem(ACTIVE_GYM_STORAGE_KEY) || undefined,
  );
  const [viewModePreference, setViewModePreference] = useState<
    ViewMode | undefined
  >((localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode) || undefined);

  const fetchMemberships = useCallback(async () => {
    try {
      const response = await api.get<IMembership[]>('users/memberships');
      setMemberships(response.data);
    } catch {
      setMemberships([]);
    }
  }, []);

  const loadingStoragedData = useCallback(async (): Promise<void> => {
    const token = localStorage.getItem('@TrenzoX:token');
    const user = localStorage.getItem('@TrenzoX:user');
    const storedActiveGymId = localStorage.getItem(ACTIVE_GYM_STORAGE_KEY);

    if (token && user) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      if (storedActiveGymId) {
        api.defaults.headers.common['x-gym-id'] = storedActiveGymId;
      }

      setData({ token, user: JSON.parse(user) });
      await fetchMemberships();
    }
    setLoading(false);
  }, [fetchMemberships]);

  const signOut = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (refreshToken) {
      try {
        await api.post('session/logout', { refresh_token: refreshToken });
      } catch {
        // best-effort -- a sessão local é limpa de qualquer forma, e o
        // registro no servidor expira sozinho se essa chamada falhar
      }
    }

    localStorage.removeItem('@TrenzoX:token');
    localStorage.removeItem('@TrenzoX:user');
    localStorage.removeItem(ACTIVE_GYM_STORAGE_KEY);
    localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    api.defaults.headers.common.Authorization = '';
    delete api.defaults.headers.common['x-gym-id'];

    setMemberships([]);
    setActiveGymId(undefined);
    setViewModePreference(undefined);
    setData({} as AuthState);
  }, []);

  const signIn = useCallback(
    async ({ user, token, refresh_token }) => {
      setLoading(true);

      localStorage.setItem('@TrenzoX:token', token);
      localStorage.setItem('@TrenzoX:user', JSON.stringify(user));
      localStorage.removeItem(ACTIVE_GYM_STORAGE_KEY);
      localStorage.removeItem(VIEW_MODE_STORAGE_KEY);

      if (refresh_token) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh_token);
      }

      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      delete api.defaults.headers.common['x-gym-id'];

      setActiveGymId(undefined);
      setViewModePreference(undefined);
      setData({ token, user });

      await fetchMemberships();

      setLoading(false);
    },
    [fetchMemberships],
  );

  const updateUser = useCallback(
    async (user: IUser) => {
      localStorage.setItem('@TrenzoX:user', JSON.stringify(user));
      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token],
  );

  // Troca de academia ativa (usuário com mais de um vínculo) — muda o
  // header `x-gym-id` que passa a ser enviado em toda requisição,
  // confirma o vínculo novo com um `GET users` de verdade (não confia
  // só na label escolhida no select) e recarrega a página inteira.
  // O reload é de propósito: a maioria das telas busca os próprios
  // dados só na montagem (`useEffect(..., [])`), sem reagir a troca de
  // academia -- sem isso, dashboard/diretório/relatórios continuavam
  // mostrando dado do vínculo anterior até um F5 manual.
  const switchGym = useCallback(async (gym_id: string) => {
    api.defaults.headers.common['x-gym-id'] = gym_id;

    const response = await api.get<IUser>('users');

    localStorage.setItem(ACTIVE_GYM_STORAGE_KEY, gym_id);
    localStorage.setItem('@TrenzoX:user', JSON.stringify(response.data));

    window.location.reload();
  }, []);

  // Sem `manage_platform_gyms` não existe conceito de modo — sempre `gym`.
  // Com a permissão, usa a preferência salva; sem preferência salva ainda,
  // abre em `platform` por padrão (documentado como prioridade do Pedro).
  const hasPlatformPermission = !!data.user?.permissions?.includes(
    'manage_platform_gyms',
  );

  const viewMode: ViewMode = hasPlatformPermission
    ? viewModePreference ?? 'platform'
    : 'gym';

  const setViewMode = useCallback((mode: ViewMode) => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    setViewModePreference(mode);
  }, []);

  useEffect(() => {
    loadingStoragedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUserData(data.user);
    setUserToken(data.token);
  }, [data]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user as IUser,
        token: data.token,
        memberships,
        activeGymId,
        switchGym,
        viewMode,
        setViewMode,
        loadingStoragedData,
        signIn,
        signOut,
        updateUser,
        loading,
        setLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
